import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { site } from "../content";
import { Icon } from "./Icon";
import { WaCta } from "./WaCta";
import { scrollToId } from "../lib/scroll";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden"
    >
      <div className="spotlight-gold pointer-events-none absolute inset-0" aria-hidden="true" />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-10 pt-40 md:px-12"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-gold" aria-hidden="true" />
          <span className="font-serif text-lg italic text-gold md:text-xl">
            Aquecedor de toalhas profissional para barbearias
          </span>
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-bone sm:text-5xl lg:text-6xl"
        >
          {site.hero.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block font-serif text-5xl font-medium normal-case italic tracking-normal text-gold sm:text-6xl lg:text-7xl"
              custom={site.hero.lines.length}
              variants={lineVariants}
              initial="hidden"
              animate="visible"
            >
              {site.hero.accentLine}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-bone/70 md:text-lg"
          data-testid="hero-subtitle"
        >
          {site.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <WaCta
            message={site.messages.hero}
            location="hero"
            label="Quero conhecer o Barber Steamer"
            testId="hero-whatsapp-btn"
          />
          <button
            onClick={() => scrollToId("como-funciona")}
            data-testid="hero-video-btn"
            className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-bone/25 bg-black/30 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-bone backdrop-blur-sm transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
          >
            Ver como funciona
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
          </button>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md sm:grid-cols-3 lg:grid-cols-5"
          data-testid="hero-indicators"
        >
          {site.hero.indicators.map((item) => (
            <li
              key={item.label}
              className="flex items-center gap-3 bg-graphite/60 px-5 py-4"
            >
              <Icon name={item.icon} className="h-4 w-4 shrink-0 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-bone/75">
                {item.label}
              </span>
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}
